<script setup>
import { ref, onMounted, watch, nextTick, computed } from 'vue';

import Avatar from './Avatar.vue';
import PrimaryButton from './PrimaryButton.vue';
import ProfileRequest from './ProfileRequest.vue';

import moment from 'moment/dist/moment';
import de from 'moment/dist/locale/de';

import { dispatchNotification } from './Notification';

import { useStore } from 'vuex';

import { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'

import {
    PlusIcon,
    ArrowLongLeftIcon,
    ArrowLongRightIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    HomeIcon,
    FaceFrownIcon,
    FaceSmileIcon,
    FireIcon,
    HandThumbUpIcon,
    HeartIcon,
    PaperClipIcon,
    CheckIcon,
    EllipsisVerticalIcon
} from '@heroicons/vue/20/solid'
import {
    ArrowDownTrayIcon, ChatBubbleLeftRightIcon, CheckBadgeIcon, LinkIcon, TrashIcon, UserPlusIcon, XMarkIcon, ExclamationTriangleIcon,
    XCircleIcon,
    CheckCircleIcon,
    InformationCircleIcon
} from '@heroicons/vue/24/outline';

const initialFormState = {
    message: '',
    file: '',
    errors: '',
    processing: false,
};

const form = ref({ ...initialFormState });

const resetForm = () => {
    resetFile();
    Object.assign(form.value, initialFormState);
}

const props = defineProps({
    user_id: String,
    user_name: String,
    user_lastname: String,
    user_image: String,
    message_view: Boolean
});

const emit = defineEmits(['messageView']);

const messageContainer = ref();
const fileInput = ref();
const channels = ref({});
const messages = ref({});
const channelTimeouts = ref([]);
const typingChannels = ref(new Set());
const activeChannel = ref();
const loadingMessages = ref(false);
const loadingChannels = ref(false);
const errorMessages = ref(false);
const errorChannels = ref(false);
const currentTime = ref(new Date());
const showEditFileModal = ref(false);
const fileToEdit = ref();
const messageToEdit = ref();

const store = useStore();
const initialChannel = computed(() => store.state.initialChannel);
const csrfToken = store.state.csrfToken;

const listenForChannelUpdates = (channel) => {
    Echo.private(`channels.${channel.id}`)
        .listen('.new.message', (e) => {
            channel.last_message = e.message;

            if (e.message.user.id != props.user_id) {
                channel.new = channel.new + 1;
            }

            typingChannels.value.delete(channel.id);
            if (channel.id == activeChannel.value) {
                messages.value.data.push(e.message);
                nextTick(() => messageContainer.value.scrollTop = messageContainer.value.scrollHeight);
            }
        }).listen('.read.message', (e) => {
            if (channel.last_message.id == e.message.id) channel.last_message = e.message;
            if (channel.id == activeChannel.value) {
                const messageIndex = messages.value.data.findIndex(message => message.id === e.message.id);
                if (messageIndex !== -1) {
                    messages.value.data[messageIndex] = e.message;
                }
            }
        }).listen('.remove.channel', (e) => {
            channels.value.data = channels.value.data.filter(channel => channel.id !== e.channel.id);
            if (activeChannel.value === e.channel.id) activeChannel.value = null;
            Echo.leave(`channels.${channel.id}`);
        }).listenForWhisper('typing', () => {
            typingChannels.value.add(channel.id);
            const newTimeout = setTimeout(() => typingChannels.value.delete(channel.id), 2000);
            const channelTimeout = channelTimeouts.value.find(timeout => timeout.id === channel.id);
            if (channelTimeout) {
                clearTimeout(channelTimeout.timeout);
                channelTimeout.timeout = newTimeout;
            } else {
                channelTimeouts.value.push({ id: channel.id, timeout: newTimeout });
            }
        });
}

const listenForNewChannells = () => {
    Echo.private(`users.${props.user_id}.channels`)
        .listen('.new.channel', (e) => {
            channels.value.data.unshift(e.channel);
            listenForChannelUpdates(channels.value.data[0]);
        });
}

const fetchChannels = async () => {
    loadingChannels.value = true;
    errorChannels.value = false;
    fetch('/api/channels', {
        headers: {
            Accept: 'application/json'
        }
    }).then(async (response) => {
        channels.value = await response.json();
        loadingChannels.value = false;
        channels.value.data?.forEach(channel => {
            listenForChannelUpdates(channel);
        });

        nextTick(() => {
            if (initialChannel.value == null && filteredChannels.value.length) {
                activeChannel.value = filteredChannels.value[0].id;
            } else if (channels.value.data?.find(channel => initialChannel.value == channel.id)) {
                activeChannel.value = parseInt(initialChannel.value);
                emit('messageView', getChannelReceiver(getChannel(activeChannel.value)));
            }
        });
    }).catch(() => errorChannels.value = true);
}

watch(channels, () => {
    channels.value.data = channels.value.data?.sort((b, a) => {
        const dateA = a.last_message ? new Date(a.last_message.created_at) : new Date(a.created_at);
        const dateB = b.last_message ? new Date(b.last_message.created_at) : new Date(b.created_at);

        if (!b.last_message && a.last_message) {
            return -1;
        } else if (b.last_message && !a.last_message) {
            return 1;
        }

        return dateA - dateB;
    });
}, { deep: true });

watch(activeChannel, () => {
    if (activeChannel.value) {
        fetchMessages(activeChannel.value);
    } else {
        messages.value.data = [];
    }
}, { deep: true });

const whisperWriting = () => {
    readMessages();
    Echo.private(`channels.${activeChannel.value}`).whisper('typing', { userId: props.user_id });
};

const fetchMessages = async (id) => {
    loadingMessages.value = true;
    errorMessages.value = false;
    fetch(`/api/channels/${id}/messages`, {
        headers: {
            Accept: 'application/json'
        }
    }).then(async (response) => {
        messages.value = await response.json();
        loadingMessages.value = false;
        nextTick(() => {
            messageContainer.value.scrollTop = messageContainer.value.scrollHeight;
            readMessages();
        })
    }).catch(() => errorMessages.value = true);
}

const sendMessage = () => {
    if (!(form.value.message || form.value.file) || form.value.processing) return;

    form.value.processing = true;
    const formData = new FormData();
    formData.append('channel_id', activeChannel.value);
    Object.entries(form.value).forEach(([key, value]) => {
        formData.append(key, value ?? '');
    });

    fetch('/api/messages', {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'X-CSRF-TOKEN': csrfToken
        },
        body: formData
    }).then(async (response) => {
        form.value.processing = false;
        form.value.errors = null;

        if (response.status === 422) {
            dispatchNotification({ title: 'Bitte überprüfe deine Eingaben.', content: 'Fehlercode: ' + response.status, type: 'error', duration: 5000 });
        } else if (response.status === 200) {
            resetForm();
        }
    }).catch((error) => {
        console.log(error);
    });
};

setInterval(() => {
    currentTime.value = new Date().getTime() + 1000;
}, 1000);

const formatDate = (date) => moment(date).from(currentTime.value);

const readMessages = () => {
    if (channels.value.data?.find(channel => activeChannel.value === channel.id)?.new) {
        fetch(`/api/channels/${activeChannel.value}/messages/read`, {
            headers: {
                Accept: 'application/json'
            }
        }).then(() => {
            const channel = channels.value.data?.find(channel => activeChannel.value === channel.id);
            if (channel) channel.new = 0;
        });
    }
}

const resetFile = () => {
    fileInput.value.value = null;
    form.value.file = null;
}

const formatFileSize = (value) => {
    let sizes = ['Bytes', 'kB', 'MB', 'GB', 'TB'];
    if (value == 0) return '0 Byte';
    let i = parseInt(Math.floor(Math.log(value) / Math.log(1024)));
    return Math.round(value / Math.pow(1024, i), 2) + ' ' + sizes[i];
}

const fileIsImage = (file) => {
    return ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/webp', 'image/svg+xml'].includes(file.mime);
}

const deleteChannel = (id) => {
    fetch(`/api/channels/${id}`, {
        method: 'DELETE',
        headers: {
            Accept: 'application/json',
            'X-CSRF-TOKEN': csrfToken
        }
    }).then(() => {
        channels.value.data = channels.value.data.filter(channel => channel.id !== id);
        if (activeChannel.value === id) activeChannel.value = null;
        dispatchNotification({ title: 'Aktion erfolgreich', content: 'Der Chat wurde erfolgreich gelöscht.', type: 'success', duration: 5000 });
        Echo.leave(`channels.${id}`);
    }).catch((error) => console.log(error));
}

const filteredChannels = computed(() => {
    return channels.value.data?.filter(channel => {
        return channel.last_message || activeChannel.value == channel.id
    });
})

const getChannelReceiver = (channel) => {
    if (!channel) return;
    return channel.initiator.id == props.user_id ? channel.receiver : channel.initiator;
}

const getChannel = (id) => {
    return channels.value.data?.find(channel => id == channel.id);
}

onMounted(() => {
    fetchChannels();
    listenForNewChannells();
});

const editFile = (message) => {
    fileToEdit.value = message.file;
    messageToEdit.value = message;
    showEditFileModal.value = true;
}

const updateFile = (file) => {
    const messageIndex = messages.value.data.findIndex(currentMessage => currentMessage.id === messageToEdit.value.id);
    if (messageIndex !== -1) {
        messages.value.data[messageIndex].file = file.data;
    }
    showEditFileModal.value = false;
}

const switchChannel = (id) => {
    activeChannel.value = id;
    emit('messageView', getChannelReceiver(getChannel(activeChannel.value)));
}

</script>

<template>
    <div class="tw-flex tw-flex-col md:tw-flex-row tw-h-[calc(100%-89px)]">

        <aside
            :class="['tw-w-full md:tw-w-96 tw-h-full tw-overflow-y-auto tw-bg-gray-100', props.message_view ? 'max-md:tw-hidden' : '']">
            <template v-if="errorChannels">
                <div class="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-flex-col">
                    <ExclamationTriangleIcon class="tw-h-8 tw-w-auto tw-text-gray-400" aria-hidden="true" />
                    <span class="tw-text-gray-400 tw-text-sm tw-px-16 tw-text-center">Es ist ein Fehler aufgetreten.
                        Bitte
                        versuche es später erneut.</span>
                </div>
            </template>
            <template v-else-if="loadingChannels">
                <div class="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-flex-col">
                    <svg aria-hidden="true" class="tw-w-6 tw-h-auto tw-text-gray-200 tw-animate-spin tw-fill-gray-600"
                        viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                            fill="currentColor" />
                        <path
                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                            fill="currentFill" />
                    </svg>
                    <span class="tw-text-gray-400 tw-text-sm">Chats werden geladen...</span>
                </div>
            </template>
            <template v-else-if="!filteredChannels?.length">
                <div class="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-flex-col">
                    <ChatBubbleLeftRightIcon class="tw-h-8 tw-w-auto tw-text-gray-400" aria-hidden="true" />
                    <span class="tw-text-gray-400 tw-text-sm tw-px-16 tw-text-center">Du hast noch keine Nachrichten.
                        Wenn du
                        aktive Chats hast, werden diese hier angezeigt.</span>
                </div>
            </template>
            <template v-else>
                <ul role="list" class="tw-gap-y-1 tw-flex tw-flex-col tw-p-2 tw-mb-0">
                    <li v-for="channel in filteredChannels" :key="channel.id"
                        class="tw-flex tw-gap-x-4 tw-py-5 tw-px-3 tw-pr-5 bg-gray-600/5 hover:tw-bg-gray-600/10 max-md:tw-bg-gray-600/5 tw-cursor-pointer tw-rounded-md"
                        :class="[channel.id == activeChannel ? 'md:!tw-bg-gray-600/20' : '']"
                        @click="switchChannel(channel.id)">

                        <div class="tw-relative tw-flex tw-shrink-0 tw-group">

                            <Avatar class="tw-h-10 tw-w-10 text-md group-hover:tw-opacity-0"
                                :initials="getChannelReceiver(channel).firstname.substring(0, 1) + getChannelReceiver(channel).lastname?.substring(0, 1)"
                                :path="getChannelReceiver(channel).avatar" />

                            <Menu as="div" class="tw-absolute tw-inset-0 tw-h-10 tw-w-10" @click.stop>
                                <MenuButton
                                    class="tw-opacity-0 group-hover:tw-opacity-100 tw-flex tw-w-full tw-h-full tw-items-center tw-justify-center tw-rounded-full tw-text-gray-400 tw-bg-gray-100 hover:tw-text-gray-600 focus:tw-outline-none">
                                    <EllipsisVerticalIcon class="tw-h-5 tw-w-5" aria-hidden="true" />
                                </MenuButton>

                                <transition enter-active-class="tw-transition tw-ease-out tw-duration-100"
                                    enter-from-class="tw-transform tw-opacity-0 tw-scale-95"
                                    enter-to-class="tw-transform tw-opacity-100 tw-scale-100"
                                    leave-active-class="tw-transition tw-ease-in tw-duration-75"
                                    leave-from-class="tw-transform tw-opacity-100 tw-scale-100"
                                    leave-to-class="tw-transform tw-opacity-0 tw-scale-95">
                                    <MenuItems
                                        class="tw-absolute tw-left-0 tw-top-7 tw-z-10 tw-mt-2 tw-w-52 tw-origin-top-right tw-rounded-md tw-bg-white tw-shadow-lg tw-ring-1 tw-ring-black tw-ring-opacity-5 focus:tw-outline-none">
                                        <div class="tw-py-1">
                                            <MenuItem v-slot="{ active }">


                                            <a v-if="getChannelReceiver(channel).role == 'company'"
                                                :href="'/unternehmen/profile/' + getChannelReceiver(channel).profile_id"
                                                :class="[active ? 'tw-bg-gray-100 tw-text-gray-900 hover:tw-text-gray-900' : 'tw-text-gray-700', 'tw-px-4 tw-py-2 tw-text-sm tw-flex tw-w-full hover:tw-no-underline']">
                                                Profil anzeigen
                                            </a>
                                            <a v-else :href="'/profile/' + getChannelReceiver(channel).profile_id"
                                                :class="[active ? 'tw-bg-gray-100 tw-text-gray-900 hover:tw-text-gray-900' : 'tw-text-gray-700', 'tw-px-4 tw-py-2 tw-text-sm tw-flex tw-w-full hover:tw-no-underline']">
                                                Profil anzeigen
                                            </a>

                                            </MenuItem>
                                            <MenuItem v-slot="{ active }">
                                            <a @click="deleteChannel(channel.id)"
                                                :class="[active ? 'tw-bg-gray-100 tw-text-red-900 hover:tw-text-red-900' : 'tw-text-red-700', 'tw-px-4 tw-py-2 tw-text-sm tw-flex tw-w-full hover:tw-no-underline']">
                                                Chat löschen
                                            </a>
                                            </MenuItem>
                                        </div>
                                    </MenuItems>
                                </transition>
                            </Menu>
                        </div>

                        <div class="tw-min-w-0 tw-w-full">
                            <div class="tw-flex">
                                <p class="tw-text-sm tw-font-semibold tw-truncate tw-leading-3 tw-text-gray-900 tw-mt-1 tw-mb-0"
                                    :title="getChannelReceiver(channel).fullname">{{
                getChannelReceiver(channel).fullname }}
                                    <span v-if="channel.reference">| #{{ channel.reference }}</span>
                                </p>
                                <div class="tw-flex-1 tw-ml-2 tw-text-right tw-leading-3 tw-whitespace-nowrap">
                                    <span v-if="channel.last_message" class="tw-text-xs tw-text-gray-500">{{
                formatDate(channel.last_message?.created_at)
            }}</span>
                                </div>
                            </div>

                            <div class="tw-flex tw-items-center">
                                <p class="tw-mt-1 tw-text-sm tw-text-gray-600 tw-line-clamp-2 tw-mb-0"
                                    :class="[channel.new ? 'tw-font-bold' : '', channel.classes]">
                                    <span v-if="typingChannels.has(channel.id)"
                                        class="tw-text-green-600 tw-italic tw-font-normal">
                                        Schreibt...
                                    </span>
                                    <span v-else-if="channel.last_message">{{ props.user_id ==
                channel.last_message?.user.id ? 'Du' :
                channel.last_message?.user.fullname }}:
                                        {{ channel.last_message?.message ?? channel.last_message?.file?.name }}</span>
                                    <span v-else class="tw-italic">
                                        Keine Nachrichten
                                    </span>
                                </p>
                                <div v-if="channel.new" class="tw-flex-1 tw-ml-2">
                                    <span
                                        class="tw-ml-auto tw-rounded-full tw-bg-gray-600 tw-text-center tw-text-[0.6rem] tw-font-extrabold tw-text-white tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0"
                                        aria-hidden="true">{{ channel.new }}</span>
                                </div>
                                <div v-else-if="channel.last_message?.user.id == props.user_id && channel.last_message.read && !typingChannels.has(channel.id)"
                                    class="tw-flex-1 tw-ml-2">
                                    <span
                                        class="tw-ml-auto tw-flex tw-flex-shrink-0 tw-justify-end tw-items-center tw-text-green-700 tw-text-xs">
                                        <CheckIcon class="tw-h-3 tw-w-auto tw-mr-0.5" />
                                        Gelesen
                                    </span>
                                </div>
                            </div>
                        </div>
                    </li>
                </ul>
            </template>
        </aside>

        <main :class="['tw-border-l tw-flex-1 tw-h-full tw-relative', props.message_view ? '' : 'max-md:tw-hidden']">
            <div class="tw-h-[calc(100%-175px)] tw-overflow-y-auto" ref="messageContainer" @click="readMessages">
                <template v-if="errorMessages">
                    <div class="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-flex-col">
                        <ExclamationTriangleIcon class="tw-h-8 tw-w-auto tw-text-gray-400" aria-hidden="true" />
                        <span class="tw-text-gray-400 tw-text-sm tw-px-16 tw-text-center">Es ist ein Fehler aufgetreten.
                            Bitte versuche es später erneut.</span>
                    </div>
                </template>
                <template v-else-if="loadingMessages">
                    <div class="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-flex-col">
                        <svg aria-hidden="true"
                            class="tw-w-6 tw-h-auto  tw-text-gray-200 tw-animate-spin tw-fill-gray-600"
                            viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                fill="currentColor" />
                            <path
                                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                fill="currentFill" />
                        </svg>
                        <span class="tw-text-gray-400 tw-text-sm">Nachrichten werden geladen...</span>
                    </div>
                </template>
                <template v-else>

                    <div v-if="getChannel(activeChannel) && !getChannel(activeChannel)?.profile_request && !getChannelReceiver(getChannel(activeChannel))?.visibility"
                        class="tw-flex tw-justify-center tw-bg-gray-900 tw-px-6 tw-py-3.5 sm:tw-px-5 tw-absolute tw-w-full tw-z-10">

                        <div class="tw-flex tw-justify-between tw-w-full tw-items-center">
                            <p class="tw-leading-4 tw-text-white tw-mb-0">
                                <span class="tw-font-bold tw-block tw-text-sm">{{
                getChannelReceiver(getChannel(activeChannel))?.firstname
            }}
                                    hat ein anonymes
                                    Profil.</span>
                                <span class="tw-text-xs">Möchtest
                                    du eine Anfrage senden, um alle Profildetails sehen zu können?</span>
                            </p>

                            <ProfileRequest request-type="send"
                                :user-id="getChannelReceiver(getChannel(activeChannel))?.id"
                                @clicked="getChannel(activeChannel).profile_request = 1">
                                <button
                                    class="tw-rounded-full tw-bg-blue-700 tw-px-4 tw-py-2 tw-text-white tw-mt-1 tw-inline-block tw-text-sm hover:tw-bg-blue-800">Anfrage
                                    senden</button>
                            </ProfileRequest>

                        </div>

                    </div>

                    <div
                        :class="[!getChannel(activeChannel)?.profile_request ? 'tw-pt-24' : '', 'tw-flex tw-flex-col tw-space-y-5 tw-p-5 tw-pb-5']">

                        <div v-for="message in messages.data" :key="message.id"
                            :class="[message.user.id == props.user_id ? 'tw-justify-end' : '', 'tw-flex tw-items-start tw-gap-x-4']">
                            <div
                                :class="[message.user.id == props.user_id ? 'tw-order-1' : 'order-0', 'tw-flex-shrink-0']">
                                <Avatar class="tw-h-10 tw-w-10 text-md"
                                    :initials="message.user.firstname.substring(0, 1) + message.user.lastname?.substring(0, 1)"
                                    :path="message.user.avatar" />
                            </div>
                            <div class="tw-w-2/3 tw-max-w-md">

                                <template v-if="message.type == 0">
                                    <div
                                        :class="[message.user.id == props.user_id ? 'tw-bg-gray-600/10 tw-text-gray-800' : 'tw-bg-gray-600/10 text-lippkau-800', 'tw-p-3 tw-rounded-lg tw-text-sm']">

                                        {{ message.message }}

                                        <div v-if="message.file"
                                            :class="[message.user.id == props.user_id ? 'tw-bg-gray-600/5 border-gray-600/20' : 'tw-bg-gray-300/5 tw-border-lippkau-600/20', 'tw-divide-y tw-rounded-md tw-border tw-border-dashed tw-mt-3']">
                                            <div
                                                class="tw-flex tw-items-center tw-flex-col tw-p-4 tw-text-sm tw-leading-6 tw-min-w-0 tw-relative group">

                                                <a v-if="fileIsImage(message.file)"
                                                    class="tw-aspect-video tw-rounded-md tw-overflow-hidden tw-w-full tw-h-full tw-bg-cover tw-bg-center"
                                                    :style="'background-image: url(' + message.file.url + ')'"
                                                    :href="message.file.url">
                                                </a>

                                                <div v-else
                                                    class="tw-flex tw-items-center tw-flex-col tw-max-w-full tw-truncate">
                                                    <PaperClipIcon class="tw-h-8 tw-w-auto tw-flex-shrink-0"
                                                        aria-hidden="true" />
                                                    <span class="tw-truncate tw-font-medium tw-w-full">{{
                message.file.name
            }}</span>
                                                    <span
                                                        :class="[message.user.id == props.user_id ? 'text-gray-800/40' : 'text-lippkau-800/40', 'tw-flex-shrink-0']">{{
                formatFileSize(message.file.size) }}</span>
                                                </div>

                                                <div class="tw-flex-shrink-0 tw-space-x-1 tw-mt-4">
                                                    <template v-if="!fileIsImage(message.file)">
                                                        <a :href="message.file.url"
                                                            class="tw-font-medium text-lippkau-600 hover:text-lippkau-500">Anzeigen</a>
                                                        <span class="text-lippkau-800/40">|</span>
                                                    </template>
                                                    <a :href="message.file.url"
                                                        class="tw-font-medium text-lippkau-600 hover:text-lippkau-500"
                                                        download>Download</a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <template v-else-if="message.type == 1">
                                    <div class="tw-p-3 tw-rounded-lg tw-bg-yellow-50">
                                        <div class="tw-flex">
                                            <div class="tw-flex-shrink-0">
                                                <ExclamationTriangleIcon
                                                    class="tw-h-5 tw-w-5 tw-text-yellow-400 -tw-mt-1"
                                                    aria-hidden="true" />
                                            </div>
                                            <div class="tw-ml-2" v-if="message.user.id != props.user_id">
                                                <h3 class="tw-text-sm tw-font-medium tw-text-yellow-800 tw-mb-0">Anfrage
                                                    von
                                                    {{ message.user.firstname }}
                                                </h3>
                                                <div class="tw-mt-0 tw-text-sm tw-text-yellow-700">
                                                    <p class="tw-mb-1">{{ message.user.firstname }} möchte gerne deine
                                                        vollständigen
                                                        Profilinformationen einsehen
                                                        können. Dazu gehören:</p>
                                                    <ul class="tw-ml-1">
                                                        <li>Vorname und Nachname</li>
                                                        <li>Profilbild</li>
                                                        <li>Kontaktinformationen</li>
                                                        <li>Lebenslauf und Interessen</li>
                                                    </ul>
                                                </div>
                                                <div v-if="!message.answered" class="tw-mt-3">
                                                    <div class="-tw-mx-2 -tw-my-1.5 tw-flex">
                                                        <ProfileRequest request-type="accept"
                                                            @clicked="message.answered = true"
                                                            :user-id="getChannelReceiver(getChannel(activeChannel))?.id">
                                                            <button type="button"
                                                                class="tw-rounded-md tw-bg-yellow-50 tw-px-3 tw-py-1.5 tw-text-sm tw-font-semibold tw-text-yellow-700 tw-bg-yellow-100 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-yellow-600 focus:tw-ring-offset-2 focus:tw-ring-offset-yellow-50">Akzeptieren</button>
                                                        </ProfileRequest>

                                                        <ProfileRequest request-type="deny"
                                                            @clicked="message.answered = true"
                                                            :user-id="getChannelReceiver(getChannel(activeChannel))?.id">
                                                            <button type="button"
                                                                class="tw-rounded-md tw-bg-yellow-50 tw-px-3 tw-py-1.5 tw-text-sm tw-font-semibold tw-text-yellow-700 tw-bg-yellow-100 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-yellow-600 focus:tw-ring-offset-2 focus:tw-ring-offset-yellow-50">Ablehnen</button>
                                                        </ProfileRequest>
                                                    </div>
                                                </div>
                                                <div v-else
                                                    class="tw-text-yellow-700 tw-bg-yellow-100 tw-px-3 tw-py-1.5 tw-text-xs tw-italic">
                                                    <InformationCircleIcon
                                                        class="tw-w-3.5 tw-h-auto tw-mr-1 tw-mb-0.5" />
                                                    Diese Anfrage wurde erfolgreich beantwortet.
                                                </div>
                                            </div>
                                            <div class="tw-ml-2" v-else>
                                                <h3 class="tw-text-sm tw-font-medium tw-text-yellow-800 tw-mb-0">Deine
                                                    Anfrage wurde
                                                    gesendet!
                                                </h3>
                                                <div class="tw-mt-0 tw-text-sm tw-text-yellow-700">
                                                    <p class="tw-mb-0">Bitte warte, bis {{
                getChannelReceiver(getChannel(activeChannel))?.firstname }}
                                                        deine
                                                        Anfrage beantwortet.</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <template v-else-if="message.type == 2">
                                    <div class="tw-p-3 tw-rounded-lg tw-bg-green-50">
                                        <div class="tw-flex">
                                            <div class="tw-flex-shrink-0">
                                                <CheckCircleIcon class="tw-h-5 tw-w-5 tw-text-green-400 -tw-mt-1"
                                                    aria-hidden="true" />
                                            </div>
                                            <div class="tw-ml-2">
                                                <h3 class="tw-text-sm tw-font-medium tw-text-green-800 tw-mb-0">{{
                message.user.firstname }} hat deine Anfrage akzeptiert!
                                                </h3>
                                                <div class="tw-mt-0 tw-text-sm tw-text-green-700">
                                                    <p class="tw-mb-0">Du kannst jetzt das vollständige Profil von {{
                message.user.firstname }} sehen.</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <template v-else-if="message.type == 3">
                                    <div class="tw-p-3 tw-rounded-lg tw-bg-red-50">
                                        <div class="tw-flex">
                                            <div class="tw-flex-shrink-0">
                                                <XCircleIcon class="tw-h-5 tw-w-5 tw-text-red-400 -tw-mt-1"
                                                    aria-hidden="true" />
                                            </div>
                                            <div class="tw-ml-2">
                                                <h3 class="tw-text-sm tw-font-medium tw-text-red-800 tw-mb-0">{{
                message.user.firstname }} hat deine Anfrage abgelehnt!
                                                </h3>
                                                <div class="tw-mt-0 tw-text-sm tw-text-red-700">
                                                    <p class="tw-mb-0">{{ message.user.firstname }} möchte nicht, dass
                                                        du
                                                        alle Profildetails sehen kannst.</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <span
                                    class="tw-text-xs tw-text-gray-400 tw-items-center tw-flex tw-justify-between tw-mt-2">
                                    {{ formatDate(message.created_at) }}
                                    <span v-if="message.user.id == props.user_id && message.read"
                                        class="tw-text-green-700/50 tw-flex tw-items-center tw-text-xs">
                                        <CheckIcon class="tw-h-3 tw-w-auto tw-mr-0.5" />
                                        Gelesen {{ formatDate(message.updated_at) }}
                                    </span>
                                </span>
                            </div>
                        </div>
                    </div>
                </template>
            </div>

            <div class="tw-flex tw-items-end tw-gap-x-4 md:tw-p-5 tw-h-[175px] tw-bg-gray-100 tw-border-t">
                <template v-if="activeChannel">

                    <div class="tw-min-w-0 tw-flex-1 tw-h-full">
                        <form @submit.prevent="sendMessage" class="tw-relative tw-h-full">
                            <div
                                class="tw-bg-white/80 tw-backdrop-blur tw-overflow-hidden md:tw-rounded-lg tw-shadow-sm tw-ring-1 tw-ring-inset tw-ring-gray-300 focus-within:tw-ring-2 focus-within:ring-lippkau-600 tw-h-full">
                                <label for="message" class="tw-sr-only">Schreibe eine Nachricht...</label>
                                <textarea row="2" name="message" id="message" v-model="form.message"
                                    @input="whisperWriting" @click="readMessages"
                                    class="tw-block tw-w-full tw-resize-none tw-border-0 tw-bg-transparent tw-p-4 tw-text-gray-900 placeholder:tw-text-gray-400 focus:tw-ring-0 sm:tw-text-sm sm:tw-leading-6 tw-outline-0"
                                    placeholder="Schreibe eine Nachricht..."
                                    @keydown.enter.exact.prevent="sendMessage" />
                            </div>

                            <div
                                class="tw-absolute tw-inset-x-0 tw-bottom-0 tw-flex tw-justify-between tw-py-3 tw-pl-5 tw-pr-3 tw-mt-2">
                                <div class="tw-flex tw-items-center tw-space-x-5 tw-min-w-0">
                                    <div class="tw-flex tw-items-center tw-max-w-full">
                                        <input class="tw-hidden" ref="fileInput" type="file"
                                            @input="form.file = $event.target.files[0]" />

                                        <template v-if="form.file">
                                            <button type="button"
                                                class="text-lippkau-800 tw-bg-gray-600/10 hover:tw-bg-gray-600/20 hover:text-lippkau-900 tw-rounded-full tw-p-2"
                                                @click="resetFile">
                                                <XMarkIcon class="tw-h-4 tw-w-4" aria-hidden="true" />
                                            </button>
                                            <div
                                                class="tw-truncate tw-text-xs tw-text-gray-700 tw-flex tw-flex-col tw-ml-2">
                                                <span class="tw-text-xs tw-text-gray-700 tw-truncate">{{ form.file?.name
                                                    }}</span>
                                                <span class="tw-text-xs tw-text-gray-500 tw-truncate">{{
                                                    formatFileSize(form.file?.size)
                                                    }}</span>
                                            </div>
                                        </template>
                                        <template v-else>
                                            <button type="button" @click="fileInput.click()"
                                                class="tw-py-2 tw-px-3 tw-items-center tw-justify-center tw-rounded-full tw-text-gray-400 tw-bg-gray-200 hover:tw-bg-gray-300 hover:tw-text-gray-500 tw-flex">
                                                <PaperClipIcon class="tw-h-4 tw-w-4" aria-hidden="true" />
                                                <span class="tw-text-xs tw-ml-2">Datei hochladen</span>
                                            </button>
                                        </template>

                                    </div>
                                </div>
                                <div class="tw-flex-shrink-0">
                                    <PrimaryButton :loading="form.processing">Senden
                                    </PrimaryButton>
                                </div>
                            </div>
                        </form>
                    </div>
                </template>
            </div>
        </main>

    </div>
</template>
